import { Injectable } from '@angular/core';
import { Client, StompSubscription } from '@stomp/stompjs';
import { environment } from 'src/environments/environment';
import { PushMessageName } from '../models/push-message-name.model';
import { PushMessageRegistration } from '../models/push-message-registration.model';
import { PushMessage } from '../models/push-message.model';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PushMessageService {
  private subscription?: StompSubscription;
  private registrations: {
    [messageName: string]: PushMessageRegistration[];
  } = {};
  private client?: Client;

  initConnection(tenant: number): void {
    let brokerUrl = environment.brokerURL;
    if (!brokerUrl) {
      brokerUrl = environment.apiUrl.replace('https://', 'wss://');
      if (brokerUrl.substring(-1) != '/') {
        brokerUrl += '/';
      }
      brokerUrl += 'ws/';
    }

    this.client = new Client({
      brokerURL: brokerUrl,
      reconnectDelay: 5000,
      heartbeatIncoming: 20000, // Expect server to send heartbeats every 20 seconds
      heartbeatOutgoing: 20000 // Client sends heartbeats every 20 seconds
      // debug: str => {
      //   console.log('STOMP: ' + str);
      // },
      // onWebSocketClose: evt => console.warn(evt)
    });

    this.client.activate();

    this.client.onConnect = frame => {
      console.log('connect --> ' + frame);
      this.subscription = this.client?.subscribe(
        `/${tenant}/Appointment`,
        message => {
          if (message.body) {
            console.log('got message with body ' + message.body);

            const obj: PushMessage = JSON.parse(message.body);
            const regs = this.registrations[obj.name];
            if (regs) {
              regs.forEach((psr: PushMessageRegistration) => {
                psr.callback(obj);
              });
            }
          } else {
            console.log('got empty message');
          }
        }
      );
    };

    this.client.onWebSocketError = evt => {
      console.log('onWebSocketError: ' + JSON.stringify(evt));
    }

    this.client.onStompError = frame => {
      console.log('Broker reported error: ' + frame.headers['message']);
      console.log('Additional details: ' + frame.body);
    };
  }

  disconnect(): void {
    this.subscription?.unsubscribe();
    this.client?.deactivate();
  }

  /**
   * Register a message handler for push notifications
   */
  register(args: PushMessageRegistration): void {
    if (!this.registrations[args.messageName]) {
      this.registrations[args.messageName] = [];
    }
    if (this.registrations[args.messageName].length === 0) {
      this.registrations[args.messageName].push({
        ...args
      });
    }
  }

  /**
   * Deregister a message handler
   */
  deregister(handler: object): void {
    Object.keys(this.registrations).forEach(messageType => {
      this.registrations[messageType] = this.registrations[messageType].filter(
        registration => registration.handler !== handler
      );
    });
  }

  /**
   * Determines whether a handler is already registered for a particular message
   */
  isRegistered(messageName: PushMessageName, handler: object): boolean {
    return (
      this.registrations[messageName] &&
      this.registrations[messageName].some(r => r.handler === handler)
    );
  }
}
